import { withEmailCampaignsAccess } from "@crm-src/components/page-permission-guard"

import { useState, useEffect, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { statusChipColor, getStatusLabel, calculateOpenRate, sendStartedMessage } from "./campaign-format"
import { campaignSendBlockReason } from "@crm-src/components/masthead-newsletter-builder/send-validation"
import { useToast } from "@crm-src/hooks/use-toast"
import { useEmailCampaigns } from "@crm-src/hooks/useEmailCampaigns"
import useWebSocket from "@crm-src/hooks/useWebSocket"
import { PageHeader } from "@/components/ui/page-header"
import { CreateWithAiButton } from "../../../otto-agent/CreateWithAiButton"
import { PageLoader } from "@/components/ui/page-loader"
import { Pagination, paginationPropsFromMeta } from "@/components/ui/pagination"
import {
  Mail,
  Search,
  MoreHorizontal,
  Plus,
  Eye,
  Edit,
  Trash2,
  Send,
  Users,
  Play,
  Pause,
  FileText,
  AlertCircle,
  FilePenLine,
  Copy,
  ScrollText,
} from "lucide-react"

import { StatsCard } from "@crm-src/components/stats-card"
import { type EmailCampaign, resubscribeCampaignAudience } from "@crm-src/lib/store/campaignsSlice"
import usePermissions from "@crm-src/hooks/usePermissions"
import { emailCampaignsPath, emailCampaignLogsPath } from "@crm-src/lib/email-campaign-routes"
import { recipientCount } from "@crm-src/lib/recipient-count"
import { type TargetSelection } from "@crm-src/components/email-target-selector"
import CampaignFileAttachment, { type CampaignAttachedFile } from "@crm-src/components/campaign-file-attachment"
import TipTapEditor from "@crm-src/components/tiptap-editor"
import EmailLayoutSelector from "@crm-src/components/email-layout-selector"
import SendTestEmailDialog from "@crm-src/components/send-test-email-dialog"
import { OliveNewsletterBuilder } from "@crm-src/components/olive-newsletter-builder"
import SimpleTextEmailBuilder from "@crm-src/components/simple-text-email-builder"
import { useAuth } from "@crm-src/hooks/useAuth"
import { useAppSelector } from "@crm-src/lib/store"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { BulkDeleteDialog } from "@/components/ui/bulk-delete-dialog"
import { CampaignDetailsDialog } from "@crm-src/components/email-campaigns/campaign-details-dialog"

// Exported unwrapped for unit tests (the default export is wrapped in the
// email-campaigns access guard, which needs a full auth store to render).
export function EmailCampaignsPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { isSuperAdmin } = usePermissions()
  // Send Now found nobody to email because everyone in the audience
  // unsubscribed: the campaign + crm-api's sentence naming them.
  const [unsubscribedAudience, setUnsubscribedAudience] = useState<{ campaign: EmailCampaign; message: string } | null>(null)
  const { user } = useAuth()
  const tenant = useAppSelector((state) => state.auth.tenant)
  const {
    campaigns,
    stats,
    pagination,
    isLoading,
    error,
    getCampaigns,
    getCampaignStats,
    updateCampaign,
    deleteCampaign,
    bulkDeleteCampaigns,
    sendCampaign,
    scheduleCampaign,
    pauseCampaign,
    resumeCampaign,
    duplicateCampaign,
    clearError,
    updateCampaignOptimistically,
    sendTestEmail,
  } = useEmailCampaigns()
  
  const { isConnected: wsConnected, onCampaignUpdate, requestCampaignsUpdate } = useWebSocket()
  
  // State management
  const [selectedCampaign, setSelectedCampaign] = useState<EmailCampaign | null>(null)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [campaignToDelete, setCampaignToDelete] = useState<EmailCampaign | null>(null)
  
  // Bulk selection state
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<Set<string>>(new Set())
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false)
  
  // Search state
  const [searchInput, setSearchInput] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  
  // Loading state for sending campaigns
  const [sendingCampaignId, setSendingCampaignId] = useState<string | null>(null)

  // Per-row actions menu anchor (keyed by campaign id) — replaces the shadcn
  // DropdownMenu during the MUI 5 migration.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null)


  // Load campaigns and stats on component mount
  const loadCampaigns = useCallback(async () => {
    try {
      await getCampaigns({ 
        search: searchTerm,
        page: pagination.current_page,
        per_page: 25,
        include_stats: true 
      })
    } catch (error) {
      console.error('Error loading campaigns:', error)
    }
  }, [getCampaigns, searchTerm, pagination.current_page])

  useEffect(() => {
    loadCampaigns()
  }, [loadCampaigns])

  // Track if any campaigns are currently sending (for auto-poll)
  const hasSendingCampaigns = campaigns.some(c => c.status === 'sending')

  // Auto-poll for progress updates when there are campaigns in 'sending' status
  // This ensures progress updates show correctly after page refresh
  useEffect(() => {
    if (!hasSendingCampaigns) {
      return // No sending campaigns, no need to poll
    }


    // Poll every 3 seconds while campaigns are sending
    const pollInterval = setInterval(async () => {
      try {
        await getCampaigns({
          search: searchTerm,
          page: pagination.current_page,
          per_page: 25,
          include_stats: true
        })
      } catch (error) {
        console.error('Auto-poll error:', error)
      }
    }, 3000)

    return () => {
      clearInterval(pollInterval)
    }
  }, [hasSendingCampaigns, getCampaigns, searchTerm, pagination.current_page])

  // Handle pagination
  const handlePageChange = (page: number) => {
    // This will trigger loadCampaigns through the dependency
    getCampaigns({ 
      search: searchTerm,
      page: page,
      per_page: 25,
      include_stats: true 
    })
  }

  // Use ref for campaigns to avoid re-subscribing WebSocket on every campaigns change
  const campaignsRef = useRef(campaigns)
  useEffect(() => {
    campaignsRef.current = campaigns
  }, [campaigns])

  // Set up WebSocket campaign update handler (stable subscription - no campaigns dependency)
  useEffect(() => {
    const unsubscribe = onCampaignUpdate((campaignUpdate) => {

      // Convert campaign_id to string to match Redux store format
      const campaignId = String(campaignUpdate.campaign_id)


      // Update campaign optimistically in Redux store
      updateCampaignOptimistically(campaignId, {
        status: campaignUpdate.status as 'not_sent' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'failed',
        sent_count: campaignUpdate.sent_count,
        delivered_count: campaignUpdate.delivered_count,
        recipients_count: campaignUpdate.recipients_count,
        updated_at: campaignUpdate.updated_at,
      })

      // Clear loading state for this campaign (use functional update to avoid stale closure)
      setSendingCampaignId(prevId => {
        if (prevId === campaignId && (campaignUpdate.status === 'sent' || campaignUpdate.status === 'failed')) {
          return null
        }
        return prevId
      })

      // Show completion notifications
      if (campaignUpdate.status === 'sent') {
        const campaign = campaignsRef.current.find(c => c.id === campaignId)
        toast({
          title: "Campaign Sent!",
          description: `Campaign "${campaign?.subject || 'Unknown'}" sent to ${campaignUpdate.sent_count} recipients`,
        })
      } else if (campaignUpdate.status === 'failed') {
        const campaign = campaignsRef.current.find(c => c.id === campaignId)
        toast({
          title: "Campaign Failed",
          description: `Campaign "${campaign?.subject || 'Unknown'}" failed to send`,
          variant: "destructive",
        })
      }
    })

    return unsubscribe
  }, [onCampaignUpdate, updateCampaignOptimistically, toast])

  // Debounce search input
  useEffect(() => {
    // Show searching indicator when user is typing
    if (searchInput !== searchTerm) {
      setIsSearching(true)
    }
    
    const timer = setTimeout(() => {
      setSearchTerm(searchInput)
      setIsSearching(false)
      // Reset to page 1 when searching
      if (searchInput !== searchTerm) {
        getCampaigns({ 
          search: searchInput,
          page: 1,
          per_page: 25,
          include_stats: true 
        })
      }
    }, 500) // Wait 500ms after user stops typing
    
    return () => clearTimeout(timer)
  }, [searchInput, searchTerm, getCampaigns])

  // Use campaigns directly from store (server-side filtering)
  const filteredCampaigns = campaigns

  // Handle view campaign
  const handleViewCampaign = (campaign: EmailCampaign) => {
    setSelectedCampaign(campaign)
    setIsViewDialogOpen(true)
  }

  // Handle edit campaign - navigate to edit page
  const handleEditCampaign = (campaign: EmailCampaign) => {
    // Prevent editing sent or sending campaigns
    if (campaign.status === 'sent' || campaign.status === 'sending') {
      toast({
        title: "Cannot Edit Campaign",
        description: "Sent campaigns cannot be edited to maintain data integrity and audit trail. You can duplicate it to create a new version.",
        variant: "destructive",
      })
      return
    }
    navigate(emailCampaignsPath(`/edit/${campaign.id}`))
  }

  // Handle view sendout logs - per-recipient delivery state for a campaign
  // that has actually gone out (offered only for sent / sending campaigns;
  // anything earlier has no recipient rows to show).
  const handleViewSendoutLogs = (campaign: EmailCampaign) => {
    navigate(emailCampaignLogsPath(campaign.id))
  }

  // Handle duplicate campaign
  const handleDuplicateCampaign = async (campaign: EmailCampaign) => {
    try {
      await duplicateCampaign(campaign.id)
      toast({
        title: "Campaign Duplicated",
        description: `"${campaign.subject}" has been duplicated as a draft. Recipients were not copied - please select them manually.`,
      })

      // Refresh campaigns list to show the new duplicate
      await getCampaigns()
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to duplicate campaign",
        variant: "destructive",
      })
    }
  }

  // Handle delete campaign — a campaign is deletable in ANY status. Single
  // delete mirrors bulk delete: both are always available so operators can
  // clean up sent (and even stuck 'sending') campaigns. Editing a sent
  // campaign stays blocked (audit trail); deleting one does not.
  const handleDeleteCampaign = (campaign: EmailCampaign) => {
    setCampaignToDelete(campaign)
    setIsDeleteDialogOpen(true)
  }

  const confirmDeleteCampaign = async () => {
    if (!campaignToDelete) return

    try {
      await deleteCampaign(campaignToDelete.id)
      toast({
        title: "Success",
        description: "Email campaign deleted successfully",
      })
      setCampaignToDelete(null)
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete email campaign",
        variant: "destructive",
      })
      throw error
    }
  }

  // Handle send campaign
  const handleSendCampaign = async (campaign: EmailCampaign) => {
    // Per-layout pre-flight — e.g. a masthead campaign needs a registration link
    // (its always-on "Register Now" buttons). Best-effort: the list payload
    // carries content_data, so we can block here before the send round-trip.
    const layoutError = campaignSendBlockReason({ layout: campaign.layout, content_data: campaign.content_data })
    if (layoutError) {
      toast({ title: "Can't send yet", description: layoutError, variant: "destructive" })
      return
    }

    setSendingCampaignId(campaign.id)
    try {
      const result = await sendCampaign(campaign.id)

      // Only once crm-api has accepted it: the job then hands the emails to
      // Mailjet in batches of 50. crm-api's count is the deduped,
      // unsubscribe-filtered audience, so prefer it over the list's.
      const recipientCount = result?.campaign?.recipients_count ?? campaign.recipients_count ?? 0
      toast({
        title: "Sending",
        description: sendStartedMessage(recipientCount, result?.contacts_created ?? []),
        duration: 10000,
      })
      
      // Clear the sending state after successful send
      setSendingCampaignId(null)
      
      // Refresh campaigns list to update status
      await getCampaigns({ 
        search: searchTerm,
        page: pagination.current_page,
        per_page: 25,
        include_stats: true 
      })
      
    } catch (error: any) {
      setSendingCampaignId(null) // Clear loading state on error
      
      // Check if it's a timeout error
      if (error.message?.includes('taking longer than expected')) {
        toast({
          title: "Campaign may still be sending",
          description: "The campaign is taking longer than expected. Please refresh the page to check the status.",
          variant: "destructive",
          duration: 10000
        })
        // Still start polling in case it's processing
        startStatusPolling(campaign.id)
      } else if (error.code === "ALL_RECIPIENTS_UNSUBSCRIBED" && isSuperAdmin()) {
        setUnsubscribedAudience({ campaign, message: error.message })
      } else {
        toast({
          title: error.code === "ALL_RECIPIENTS_UNSUBSCRIBED" ? "Everyone has unsubscribed" : "Error",
          description: error.code === "ALL_RECIPIENTS_UNSUBSCRIBED"
            ? `${error.message} Only a super admin can resubscribe them.`
            : error.message || "Failed to send campaign",
          variant: "destructive",
        })
      }
    }
  }

  // Super admin chose "Resubscribe & send": crm-api takes them off Mailjet's
  // exclusion list and the CRM copy, then the send runs again.
  const confirmResubscribeAndSend = async () => {
    if (!unsubscribedAudience) return
    const { campaign } = unsubscribedAudience
    try {
      const emails = await resubscribeCampaignAudience(campaign.id)
      setUnsubscribedAudience(null)
      toast({ title: "Resubscribed", description: emails.join(", ") })
      await handleSendCampaign(campaign)
    } catch (error: any) {
      setUnsubscribedAudience(null)
      toast({ title: "Couldn't resubscribe", description: error.message, variant: "destructive" })
    }
  }

  // Polling fallback for campaign status updates
  const startStatusPolling = (campaignId: string) => {
    let pollCount = 0
    const maxPolls = 60 // Poll for max 5 minutes (5 seconds * 60 = 300 seconds)
    
    const pollInterval = setInterval(async () => {
      pollCount++
      
      // Stop polling after max attempts
      if (pollCount >= maxPolls) {
        clearInterval(pollInterval)
        setSendingCampaignId(null)
        return
      }
      
      try {
        // Refresh campaigns to get latest status
        await getCampaigns({ 
          search: searchTerm,
          page: pagination.current_page,
          per_page: 25,
          include_stats: true 
        })
        
        // Check if campaign is no longer sending
        const updatedCampaign = campaigns.find(c => c.id === campaignId)
        if (updatedCampaign && updatedCampaign.status !== 'sending') {
          clearInterval(pollInterval)
          setSendingCampaignId(null)
          
          // Show completion notification if not already shown by WebSocket
          if (updatedCampaign.status === 'sent') {
            toast({
              title: "Campaign Sent!",
              description: `Campaign "${updatedCampaign.subject}" sent to ${updatedCampaign.sent_count} recipients`,
            })
          } else if (updatedCampaign.status === 'failed') {
            toast({
              title: "Campaign Failed",
              description: `Campaign "${updatedCampaign.subject}" failed to send`,
              variant: "destructive",
            })
          }
        }
      } catch (error) {
        console.error('❌ Polling error:', error)
      }
    }, 5000) // Poll every 5 seconds
    
    // Clean up interval when component unmounts or campaign changes
    return () => clearInterval(pollInterval)
  }

  // Handle pause/resume campaign
  const handlePauseCampaign = async (campaign: EmailCampaign) => {
    try {
      if (campaign.status === 'scheduled') {
        await pauseCampaign(campaign.id)
        toast({
          title: "Success",
          description: `Campaign "${campaign.name}" paused successfully`,
        })
      } else if (campaign.status === 'paused') {
        await resumeCampaign(campaign.id)
        toast({
          title: "Success",
          description: `Campaign "${campaign.name}" resumed successfully`,
        })
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update campaign status",
        variant: "destructive",
      })
    }
  }

  // Bulk selection handlers
  const handleSelectCampaign = (campaignId: string) => {
    setSelectedCampaignIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId)
      } else {
        newSet.add(campaignId)
      }
      return newSet
    })
  }

  const handleSelectAllCampaigns = () => {
    if (selectedCampaignIds.size === campaigns.length) {
      setSelectedCampaignIds(new Set())
    } else {
      setSelectedCampaignIds(new Set(campaigns.map(c => c.id)))
    }
  }

  const handleBulkDeleteCampaigns = () => {
    if (selectedCampaignIds.size === 0) return
    setIsBulkDeleteDialogOpen(true)
  }

  const confirmBulkDeleteCampaigns = async (ids: string[]) => {
    if (ids.length === 0) return

    try {
      await bulkDeleteCampaigns(ids)
      toast({
        title: "Success",
        description: `Successfully deleted ${ids.length} email campaign(s)`,
      })
      setSelectedCampaignIds(new Set())
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete email campaigns",
        variant: "destructive",
      })
      throw error
    }
  }

  // Helper functions
  // Retained because CampaignDetailsDialog's prop contract still expects a
  // (status) => string. The dialog maps the chip color itself; this just
  // satisfies the type. The page renders its own chips via statusChipColor.
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'not_sent': return 'bg-amber-100 text-amber-900 border-amber-300 font-semibold'
      case 'scheduled': return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'sending': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'sent': return 'bg-green-100 text-green-800 border-green-200'
      case 'paused': return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'failed': return 'bg-red-100 text-red-800 border-red-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }


  // Handle error display
  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error,
        variant: "destructive",
      })
      // Clear error after showing it
      setTimeout(() => clearError(), 5000)
    }
  }, [error, clearError, toast])

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header — search lives here beside the primary action (the table card
          below has no header of its own). */}
      <PageHeader
        title="Email Campaigns"
        description="Create and manage email marketing campaigns"
        actions={
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { sm: "center" }, gap: 1, width: { xs: "100%", sm: "auto" } }}>
            <TextField
              size="small"
              placeholder="Search campaigns..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ width: { xs: "100%", sm: 260 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} color={isSearching ? "#2563eb" : undefined} />
                  </InputAdornment>
                ),
                ...(isSearching ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ fontSize: "0.875rem", color: "primary.main" }}>Searching...</Typography>
                    </InputAdornment>
                  ),
                } : {}),
              }}
            />
            <CreateWithAiButton skill="create_campaign" />
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => navigate(emailCampaignsPath('/create'))}
            >
              Create Campaign
            </Button>
          </Box>
        }
      />

      {/* Error Display */}
      {error && (
        <Alert severity="error" icon={<AlertCircle size={20} />}>
          <Typography component="h3" sx={{ fontSize: "0.875rem", fontWeight: 500 }}>Error</Typography>
          <Typography sx={{ mt: 1, fontSize: "0.875rem" }}>{error}</Typography>
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && !isLoading && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2 }}>
          <StatsCard
            title="Total Campaigns"
            value={stats.total_campaigns}
            icon={Mail}
            iconColor="text-blue-600"
          />
          <StatsCard
            title="Not Sent Campaigns"
            value={stats.not_sent_campaigns}
            icon={FileText}
            iconColor="text-gray-600"
          />
          <StatsCard
            title="Sent Campaigns"
            value={stats.sent_campaigns}
            icon={Send}
            iconColor="text-green-600"
          />
          <StatsCard
            title="Total Recipients"
            value={stats.total_recipients}
            icon={Users}
            iconColor="text-purple-600"
          />
        </Box>
      )}

      {/* Campaigns Table (no card header — title is the page header above,
          search moved up beside Create Campaign) */}
      <Card>
        <CardContent>
          {/* Bulk Selection Indicator - Shows above table */}
          {selectedCampaignIds.size > 0 && !isLoading && filteredCampaigns.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 1.5, mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: "divider" }}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" }}>
                Selected {selectedCampaignIds.size} {selectedCampaignIds.size === 1 ? 'item' : 'items'}
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<Trash2 size={16} />}
                onClick={handleBulkDeleteCampaigns}
              >
                Delete Selected ({selectedCampaignIds.size})
              </Button>
            </Box>
          )}

          {isLoading ? (
            <PageLoader />
          ) : filteredCampaigns.length === 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, textAlign: "center" }}>
              <Box sx={{ mx: "auto", display: "flex", width: 64, height: 64, alignItems: "center", justifyContent: "center", borderRadius: "50%", bgcolor: (t) => alpha(t.palette.primary.main, 0.12), mb: 2 }}>
                <Mail size={32} color="#2563eb" />
              </Box>
              <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary", mb: 1 }}>
                {searchInput ? "No campaigns found" : "No campaigns yet"}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 3, maxWidth: "24rem" }}>
                {searchInput ? (
                  <>Try adjusting your search criteria.</>
                ) : (
                  <>Get started by creating your first email campaign to reach your prospects and customers.</>
                )}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 50 }}>
                    <Checkbox
                      checked={selectedCampaignIds.size === campaigns.length && campaigns.length > 0}
                      onChange={handleSelectAllCampaigns}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>Subject</TableCell>
                  <TableCell sx={{ width: 120 }}>Status</TableCell>
                  <TableCell sx={{ width: 140 }}>Recipients</TableCell>
                  <TableCell sx={{ width: 130 }}>Created By</TableCell>
                  <TableCell sx={{ width: 170 }}>Dates</TableCell>
                  <TableCell sx={{ width: 180 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCampaigns.map((campaign) => (
                  <TableRow
                    key={campaign.id}
                    sx={{ "&:hover": { bgcolor: "action.hover" } }}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedCampaignIds.has(campaign.id)}
                        onChange={() => handleSelectCampaign(campaign.id)}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ cursor: "pointer", maxWidth: 300 }}
                      onClick={() => handleViewCampaign(campaign)}
                    >
                      <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{campaign.subject}</Box>
                    </TableCell>
                    <TableCell
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleViewCampaign(campaign)}
                    >
                      <Chip
                        size="small"
                        color={statusChipColor(campaign.status) ?? "default"}
                        {...(campaign.status === 'not_sent' ? { icon: <FilePenLine size={12} /> } : {})}
                        label={getStatusLabel(campaign.status)}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleViewCampaign(campaign)}
                    >
                      <Box sx={{ fontSize: "0.875rem" }}>
                        <Box>{recipientCount(campaign)} recipients</Box>
                        {campaign.status === 'sending' ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Box sx={{ color: "warning.main", fontWeight: 500 }}>
                              {campaign.sent_count}/{campaign.recipients_count} sent
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              color="warning"
                              value={campaign.recipients_count > 0
                                ? (campaign.sent_count / campaign.recipients_count) * 100
                                : 0
                              }
                              sx={{ height: 6, borderRadius: 1 }}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ color: "text.secondary" }}>{campaign.sent_count} sent</Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleViewCampaign(campaign)}
                    >
                      <Box sx={{ fontSize: "0.875rem", color: "text.primary" }}>
                        {campaign.created_by?.name || campaign.owner?.name || 'Unknown'}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleViewCampaign(campaign)}
                    >
                      <Box sx={{ fontSize: "0.75rem", display: "flex", flexDirection: "column", gap: 0.5, whiteSpace: "nowrap" }}>
                        <Box sx={{ color: "text.primary" }}>
                          <Box component="span" sx={{ fontWeight: 500 }}>Created:</Box> {new Date(campaign.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Box>
                        <Box sx={{ color: "text.secondary" }}>
                          <Box component="span" sx={{ fontWeight: 500 }}>Edited:</Box> {new Date(campaign.updated_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {campaign.status === 'not_sent' && (
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            sx={{ height: 32, px: 1.5, whiteSpace: "nowrap", flexShrink: 0 }}
                            disabled={sendingCampaignId === campaign.id || selectedCampaignIds.size > 0}
                            startIcon={sendingCampaignId === campaign.id
                              ? <CircularProgress size={16} color="inherit" />
                              : <Send size={16} />}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleSendCampaign(campaign)
                            }}
                          >
                            {sendingCampaignId === campaign.id ? 'Sending emails...' : 'Send Now'}
                          </Button>
                        )}
                        {campaign.status === 'sending' && (
                          <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            sx={{ height: 32, px: 1.5, whiteSpace: "nowrap", flexShrink: 0 }}
                            disabled={true}
                            startIcon={<CircularProgress size={16} color="inherit" />}
                          >
                            {campaign.recipients_count > 0
                              ? `Sending ${campaign.sent_count}/${campaign.recipients_count}...`
                              : 'Sending...'
                            }
                          </Button>
                        )}
                        {campaign.status === 'failed' && (
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            sx={{ height: 32, px: 1.5, whiteSpace: "nowrap", flexShrink: 0 }}
                            disabled={sendingCampaignId === campaign.id || selectedCampaignIds.size > 0}
                            startIcon={sendingCampaignId === campaign.id
                              ? <CircularProgress size={16} color="inherit" />
                              : <Send size={16} />}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleSendCampaign(campaign)
                            }}
                          >
                            {sendingCampaignId === campaign.id ? 'Sending emails...' : 'Retry Send'}
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          sx={{ width: 32, height: 32 }}
                          aria-label="Open menu"
                          onClick={(e) => { e.stopPropagation(); setMenuAnchor({ el: e.currentTarget, id: campaign.id }) }}
                        >
                          <MoreHorizontal size={16} />
                        </IconButton>
                        <Menu
                          anchorEl={menuAnchor?.id === campaign.id ? menuAnchor.el : null}
                          open={menuAnchor?.id === campaign.id}
                          onClose={() => setMenuAnchor(null)}
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          transformOrigin={{ vertical: "top", horizontal: "right" }}
                        >
                          <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Actions</ListSubheader>
                          <Divider />
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              setMenuAnchor(null)
                              handleViewCampaign(campaign)
                            }}
                          >
                            <ListItemIcon><Eye size={16} /></ListItemIcon>
                            View Details
                          </MenuItem>
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              setMenuAnchor(null)
                              handleEditCampaign(campaign)
                            }}
                            disabled={campaign.status === 'sent' || campaign.status === 'sending'}
                          >
                            <ListItemIcon><Edit size={16} /></ListItemIcon>
                            Edit Campaign
                            {(campaign.status === 'sent' || campaign.status === 'sending') && (
                              <Box component="span" sx={{ ml: 1, fontSize: "0.75rem", color: "text.secondary" }}>(Read-only)</Box>
                            )}
                          </MenuItem>
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              setMenuAnchor(null)
                              handleDuplicateCampaign(campaign)
                            }}
                          >
                            <ListItemIcon><Copy size={16} /></ListItemIcon>
                            Duplicate Campaign
                          </MenuItem>
                          {(campaign.status === 'sent' || campaign.status === 'sending') && (
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation()
                                setMenuAnchor(null)
                                handleViewSendoutLogs(campaign)
                              }}
                            >
                              <ListItemIcon><ScrollText size={16} /></ListItemIcon>
                              View Sendout Logs
                            </MenuItem>
                          )}
                          <Divider />
                          {campaign.status === 'scheduled' && (
                            <MenuItem
                              sx={{ color: "warning.main" }}
                              onClick={(e) => {
                                e.stopPropagation()
                                setMenuAnchor(null)
                                handlePauseCampaign(campaign)
                              }}
                            >
                              <ListItemIcon sx={{ color: "warning.main" }}><Pause size={16} /></ListItemIcon>
                              Pause
                            </MenuItem>
                          )}
                          {campaign.status === 'paused' && (
                            <MenuItem
                              sx={{ color: "info.main" }}
                              onClick={(e) => {
                                e.stopPropagation()
                                setMenuAnchor(null)
                                handlePauseCampaign(campaign)
                              }}
                            >
                              <ListItemIcon sx={{ color: "info.main" }}><Play size={16} /></ListItemIcon>
                              Resume
                            </MenuItem>
                          )}
                          <Divider />
                          <MenuItem
                            sx={{ color: "error.main" }}
                            onClick={(e) => {
                              e.stopPropagation()
                              setMenuAnchor(null)
                              handleDeleteCampaign(campaign)
                            }}
                          >
                            <ListItemIcon sx={{ color: "error.main" }}><Trash2 size={16} /></ListItemIcon>
                            Delete Campaign
                          </MenuItem>
                        </Menu>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          <Pagination
            {...paginationPropsFromMeta(pagination)}
            entityNoun="campaigns"
            hideIfSinglePage
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      {/* Campaign Details Dialog */}
      <CampaignDetailsDialog
        open={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
        campaign={selectedCampaign}
        getStatusColor={getStatusColor}
        getStatusLabel={getStatusLabel}
        calculateOpenRate={calculateOpenRate}
        onEdit={handleEditCampaign}
      />

      {/* Delete Confirmation Dialog.
          crm-api's campaigns#destroy calls `soft_delete!` (a bare
          `update_column(:deleted_at, …)`), and EmailCampaign's default_scope is
          `where(deleted_at: nil)` — so the campaign just leaves the list. Its
          campaign_recipients and email_trackings rows are NOT cascaded (a
          `dependent: :destroy` association never fires on a soft delete), so the
          analytics survive intact. See docs/crm.md → "Delete is a soft delete". */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Delete Campaign"
        description="The campaign is removed from the campaign list; if it is still sending, the send stops. Its content, recipients and analytics are kept, so an administrator can restore it."
        confirmLabel="Delete Campaign"
        onConfirm={confirmDeleteCampaign}
      >
        {campaignToDelete && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ borderRadius: 1, border: 2, borderColor: "#fee2e2", bgcolor: "#fef2f2", p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "text.secondary", textTransform: "uppercase", mb: 0.5 }}>Campaign Subject</Typography>
                <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "text.primary" }}>
                  {campaignToDelete.subject}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, pt: 1, borderTop: 1, borderColor: "#fecaca" }}>
                <Box>
                  <Chip size="small" color={statusChipColor(campaignToDelete.status) ?? "default"} label={getStatusLabel(campaignToDelete.status)} />
                </Box>
                {campaignToDelete.created_by?.name && (
                  <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                    Created by {campaignToDelete.created_by.name}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ bgcolor: "#fffbeb", border: 1, borderColor: "#fde68a", borderRadius: 1, p: 1.5, display: "flex", gap: 1.5 }}>
              <Box sx={{ flexShrink: 0, mt: 0.25, color: "#d97706" }}><AlertCircle size={20} /></Box>
              <Typography sx={{ fontSize: "0.875rem", color: "#92400e" }}>
                Are you sure you want to delete this campaign? Emails already delivered are unaffected.
              </Typography>
            </Box>
          </Box>
        )}
      </ConfirmDialog>

      {/* Everyone in the audience unsubscribed (super admin only) — say who,
          and offer to put them back. Resubscribing someone who opted out is a
          deliberate call, so it is a confirm, never automatic. */}
      <ConfirmDialog
        isOpen={unsubscribedAudience !== null}
        onClose={() => setUnsubscribedAudience(null)}
        title="Everyone has unsubscribed"
        description={unsubscribedAudience?.message}
        variant="warning"
        confirmLabel="Resubscribe & send"
        onConfirm={confirmResubscribeAndSend}
      >
        <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
          Only do this if they asked to receive these emails again (for example, your own test address).
          They are taken off Mailjet&apos;s unsubscribe list too.
        </Typography>
      </ConfirmDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <BulkDeleteDialog
        isOpen={isBulkDeleteDialogOpen}
        onClose={() => setIsBulkDeleteDialogOpen(false)}
        items={campaigns.filter(c => selectedCampaignIds.has(c.id))}
        entityLabel={{ singular: "Campaign", plural: "Campaigns" }}
        subtitle="They leave the campaign list; the records are kept"
        cascadingWarning="This marks the selected campaigns as deleted, so they leave the campaign list and any still sending stop. Their content, recipients and analytics are kept — an administrator can restore them."
        renderItem={(campaign) => campaign.subject}
        onConfirm={confirmBulkDeleteCampaigns}
      />

    </Box>
  )
}

export default withEmailCampaignsAccess(EmailCampaignsPage)